// Redaction for error reports (ADR 0007). Error messages and stack traces are
// produced by code we don't control (Postgres, Drizzle, the browser), so they can carry
// data values: a quoted input, a query's parameters, a phone number in a URL. Before an
// error is stored or logged, its text goes through here.
//
// This is a safety net, not a guarantee: free text can hold a name in a form no pattern
// recognises. So error reports also keep only what is needed to find the bug (class,
// code, route pattern, stack frames) and never request bodies, query strings, headers,
// cookies, form values or page text.
//
// The browser runs the same code before sending a report, and the server runs it again
// on what it receives, so a report is redacted even if the browser's copy is old.

export const REDACTED = '[redacted]';

/** The longest message kept after redaction. */
export const MAX_MESSAGE_LENGTH = 500;
/** The most stack frames kept. */
export const MAX_STACK_FRAMES = 30;

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

// Drizzle's query errors end with "params: <the values>". Everything from there goes.
const QUERY_PARAMS = /\bparams:[\s\S]*$/i;

// user:password@ in a connection string.
const URL_CREDENTIALS = /(\b[a-z][a-z0-9+.-]*:\/\/)[^\s/@]+@/gi;

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/g;

// Kenyan mobile numbers: 07xx and 01xx local, or 254 / +254 international, with
// optional spaces or dashes (the same shape scripts/check-no-phi.ts looks for).
const PHONE = /(?<![\w+])(?:\+?254[\s-]?|0)[17]\d{2}[\s-]?\d{3}[\s-]?\d{3}(?!\w)/g;

// Query strings and fragments of any URL.
const URL_QUERY = /(\b[a-z][a-z0-9+.-]*:\/\/[^\s?#'"()]*)[?#][^\s'"()]*/gi;

// Dates, which may be dates of birth.
const DATE =
  /(?<!\d)(?:\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?|\d{1,2}[/.]\d{1,2}[/.]\d{2,4})(?!\d)/g;

// "name: Jane Demo", "phone=...", "patientName = ...": the value after a field whose name
// says it holds personal data, up to the next separator.
const PERSONAL_FIELDS = [
  'name',
  'first_?name',
  'last_?name',
  'middle_?name',
  'other_?names?',
  'full_?name',
  'given_?name',
  'family_?name',
  'display_?name',
  'patient(?:_?name)?',
  'surname',
  'phone(?:_?number)?',
  'msisdn',
  'mobile',
  'tel(?:ephone)?',
  'e_?mail',
  'id_?(?:number|no)',
  'national_?id',
  'passport(?:_?(?:number|no))?',
  'birth_?cert(?:ificate)?(?:_?(?:number|no))?',
  'cr_?id',
  'sha_?(?:number|no|id)',
  'dob',
  'date_?of_?birth',
  'birth_?date',
  'address',
  'next_?of_?kin',
  'kin',
  'notes?',
  'diagnosis',
  'complaint',
  'password',
  'token',
  'secret',
  'cookie',
  'authorization',
];
const FIELD_VALUE = new RegExp(
  `(?<![\\w])(${PERSONAL_FIELDS.join('|')})(["']?\\s*[:=]\\s*)(?!\\[redacted\\])[^,;&\\n)}\\]]+`,
  'gi',
);

// Quoted values: Postgres quotes offending input, e.g. `invalid input syntax for type
// uuid: "..."`. Property names in messages are quoted too and are lost (JSON keys
// such as `"age":` are kept); the stack frames still point at the line.
const QUOTED = /"[^"\n]*"|'[^'\n]*'|`[^`\n]*`/g;

// Postgres constraint details: Key (phone)=(0700...) already exists.
const PAREN_VALUE = /=\([^)\n]*\)/g;

// Two or more capitalised words in a row, which is how a full name usually appears in
// a message ("Cannot show Jane Wanjiku Demo"). Engine and library messages are in
// lower case, so little else matches. A single name on its own is not caught.
const NAME_WORD = String.raw`\p{Lu}(?=[\p{L}'-]*\p{Ll})[\p{L}'-]+`;
const CAPITALISED_RUN = new RegExp(
  String.raw`(?<![\w'-])${NAME_WORD}(?:\s+${NAME_WORD})+(?![\w'-])`,
  'gu',
);

// Any token with six or more digits: national ID, passport, CR ID, SHA number,
// receipt and M-Pesa codes, timestamps.
const DIGIT_TOKEN = /(?<![\w-])[\w-]*\d[\w-]*(?![\w-])/g;

const QUOTED_IDENTIFIER = /^["'][A-Za-z_$][\w$]{0,63}["']$/;

/** `"age":` in JSON is a key, not a value. */
function isObjectKey(quoted: string, rest: string): boolean {
  return QUOTED_IDENTIFIER.test(quoted) && /^\s*:/.test(rest);
}

function countDigits(token: string): number {
  let n = 0;
  for (const ch of token) if (ch >= '0' && ch <= '9') n += 1;
  return n;
}

function redactSegment(text: string): string {
  return text
    .replace(URL_CREDENTIALS, `$1${REDACTED}@`)
    .replace(EMAIL, '[email]')
    .replace(URL_QUERY, '$1')
    .replace(PHONE, '[phone]')
    .replace(DATE, '[date]')
    .replace(FIELD_VALUE, `$1$2${REDACTED}`)
    .replace(QUOTED, (q, offset: number, whole: string) =>
      isObjectKey(q, whole.slice(offset + q.length))
        ? q
        : `${q.charAt(0)}${REDACTED}${q.charAt(0)}`,
    )
    .replace(PAREN_VALUE, `=(${REDACTED})`)
    .replace(CAPITALISED_RUN, '[name]')
    .replace(DIGIT_TOKEN, (token) => (countDigits(token) >= 6 ? '[number]' : token));
}

/**
 * Removes things that can identify a person from free text: emails, phone numbers,
 * dates, values of personal fields, quoted values, runs of capitalised words (full
 * names), long numbers and URL query strings.
 * UUIDs are kept; they are internal IDs, not personal data.
 */
export function redactText(text: string, maxLength = MAX_MESSAGE_LENGTH): string {
  const withoutParams = text.replace(QUERY_PARAMS, `params: ${REDACTED}`);
  // Split around UUIDs so the digit rule can't eat parts of them.
  let out = '';
  let last = 0;
  for (const m of withoutParams.matchAll(UUID)) {
    out += redactSegment(withoutParams.slice(last, m.index)) + m[0];
    last = m.index + m[0].length;
  }
  out += redactSegment(withoutParams.slice(last));
  return out.length > maxLength ? `${out.slice(0, maxLength)}…` : out;
}

// V8: "    at fn (file:///srv/wodi/apps/api/src/x.ts:10:5)"
// Firefox and Safari: "fn@https://host/assets/index-abc.js:1:2345"
const V8_FRAME = /^\s*at\s/;
const GECKO_FRAME = /^[^\s@]*@\S+:\d+:\d+$/;
const POSITION = /:\d+:\d+\)?$/;
// Absolute paths up to the repository's own folders, so frames read the same everywhere.
const REPO_PATH = /(?:file:\/\/)?\/[^\s()]*?\/((?:apps|packages|node_modules)\/)/g;
// A script URL's query string or fragment (cache busters, and anything else).
const QUERY_OR_HASH = /[?#][^\s()]*/g;
// The origin of a script URL; the path is enough.
const ORIGIN = /\bhttps?:\/\/[^/\s()]+/g;

function redactFrame(line: string): string {
  const trimmed = line.trim();
  const position = POSITION.exec(trimmed)?.[0] ?? '';
  const body = trimmed.slice(0, trimmed.length - position.length);
  const cleaned = body.replace(REPO_PATH, '$1').replace(ORIGIN, '').replace(QUERY_OR_HASH, '');
  return redactText(cleaned, 300) + position;
}

/**
 * Keeps only the frames of a stack trace, redacted. The first lines of a stack repeat
 * the message (and anything else a library put there), so they are dropped; the
 * message is stored separately after its own redaction.
 */
export function redactStack(stack: string | undefined): string | null {
  if (stack === undefined || stack === '') return null;
  const frames = stack
    .split('\n')
    .filter((line) => V8_FRAME.test(line) || GECKO_FRAME.test(line.trim()))
    .slice(0, MAX_STACK_FRAMES)
    .map(redactFrame);
  return frames.length === 0 ? null : frames.join('\n');
}

/**
 * The frames without line and column numbers, for grouping: the same bug keeps its
 * group when unrelated edits move it a few lines.
 */
export function stackSignature(redactedStack: string | null, frames = 5): string {
  if (redactedStack === null) return '';
  return redactedStack
    .split('\n')
    .slice(0, frames)
    .map((f) => f.replace(POSITION, ''))
    .join('\n');
}

const ERROR_NAME = /^[A-Za-z_$][\w$.]{0,99}$/;
const ERROR_CODE = /^[A-Za-z0-9_.-]{1,64}$/;

/** An error class name such as TypeError, or "Error" if it isn't one. */
export function safeErrorName(name: unknown): string {
  return typeof name === 'string' && ERROR_NAME.test(name) ? name : 'Error';
}

/** A machine code such as 23505 or ECONNREFUSED, or null if it isn't one. */
export function safeErrorCode(code: unknown): string | null {
  if (typeof code === 'number' && Number.isInteger(code)) return String(code);
  return typeof code === 'string' && ERROR_CODE.test(code) ? code : null;
}
